import { rescueModeMutation } from "@/admin/vm/vmQuery.ts";
import type { GQVmItem } from "@/admin/vm/VmsTable.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { availableCustomImagesAtom } from "@/modules/compute/customImagesQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { VmStartCustomRescueBody } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { type ReactNode, useState } from "react";

interface RescueModeVmBlockModalProps {
    item: GQVmItem;
}

function RescueModeVmBlockModalContent({ item }: RescueModeVmBlockModalProps) {
    const startInRescueMutation = useStandardMutation(rescueModeMutation);
    const allImages = useSuspenseQueryAtom(availableCustomImagesAtom);
    const [bootImageUuid, setBootImageUuid] = useState<string>(allImages[0]?.uuid);

    const { uuid, _location } = item;

    async function onSubmit() {
        const body: VmStartCustomRescueBody = {
            uuid,
            boot_image_uuid: bootImageUuid,
            boot_image_repository: "platform",
        };
        await startInRescueMutation.mutateAsync({ body, location: notNull(_location) });
        // TODO: start VM console
    }

    return (
        <WModalContent
            title="Boot from ISO medium"
            label="Start"
            autoFocus
            modalAction={async () => await onSubmit()}
            className="max-w-32rem"
        >
            <p className="pb-3">
                You can boot your VM from a special image for system recovery or to install a fresh operating system.
                When this mode is activated, a chosen image will be attached as a boot medium. You can then use the
                Terminal to access your machine. This boot mode is enabled only for the current launch. Next time this
                VM is started it will follow a normal routine.
            </p>

            <FormField wide headless isRequired label="Custom ISO image">
                <WSelect
                    aria-label="Select type"
                    items={allImages}
                    getKey={(item) => item.uuid}
                    getTextValue={(item) => item.description}
                    searchItems
                    onChange={(item) => setBootImageUuid(item.uuid)}
                    valueKey={bootImageUuid}
                >
                    {(item) => item.description}
                </WSelect>
            </FormField>
        </WModalContent>
    );
}

interface RescueModeVmBlockProps extends RescueModeVmBlockModalProps {
    button: ReactNode;
}

export function RescueModeVmBlock(props: RescueModeVmBlockProps) {
    return (
        <WModal button={props.button}>
            <RescueModeVmBlockModalContent {...props} />
        </WModal>
    );
}
